import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "../lib/api";
import { listTasks, createTask, updateTask, deleteTask, reorderTasks } from "../lib/offlineTasks";
import { deleteCapture } from "../lib/offlineCaptures";
import { ModuleNavBar } from "../components/ModuleNavBar";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Plus, Trash, CalendarBlank, CheckSquare, BellRinging, DotsSixVertical, CaretDown, CaretUp } from "@phosphor-icons/react";
import { useTaskReminders } from "../hooks/useTaskReminders";
import { TaskDetailDialog } from "../components/TaskDetailDialog";
import { CaptureEditDialog } from "../components/CapturePreviewDialog";
import { TasksSidePanel, OverdueTasksCard } from "../components/TasksSidePanel";

// A single task row. When `draggable` is true (the incomplete-tasks list),
// a dedicated drag handle lets the user freely reorder tasks up/down to set
// their own priority order - the click-to-edit area stays separate from the
// handle so dragging and opening the detail dialog never conflict.
function TaskRow({ task, isOverdue, isDueToday, onToggle, onDelete, onOpenDetail, draggable }) {
  const { t } = useTranslation();
  const sortable = useSortable({ id: task.id, disabled: !draggable });
  const style = draggable
    ? { transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition, opacity: sortable.isDragging ? 0.5 : 1 }
    : undefined;

  return (
    <div
      ref={draggable ? sortable.setNodeRef : undefined}
      style={style}
      data-testid={`task-item-${task.id}`}
      onClick={() => onOpenDetail(task)}
      className={`flex items-center gap-2 border bg-card px-3 py-2 cursor-pointer hover:bg-secondary/40 transition-colors ${isOverdue ? "border-red-500/50" : "border-border"}`}
    >
      {draggable && (
        <button
          data-testid={`task-drag-handle-${task.id}`}
          {...sortable.attributes}
          {...sortable.listeners}
          onClick={(e) => e.stopPropagation()}
          className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing shrink-0 touch-none"
          aria-label={t("tasks.dragHandleLabel")}
        >
          <DotsSixVertical size={16} weight="bold" />
        </button>
      )}
      <Checkbox
        data-testid={`task-checkbox-${task.id}`}
        checked={task.completed}
        onCheckedChange={() => onToggle(task)}
        onClick={(e) => e.stopPropagation()}
      />
      <span className={`flex-1 text-sm font-bold ${task.completed ? "line-through text-muted-foreground" : ""}`}>{task.title}</span>
      {task.due_date && (
        <span
          className={`flex items-center gap-1 text-xs md:text-sm font-mono-tag font-bold shrink-0 ${isOverdue ? "text-red-600" : isDueToday ? "text-amber-600" : "text-primary"}`}
          data-testid={`task-due-date-${task.id}`}
        >
          {isOverdue ? <BellRinging size={13} weight="fill" /> : <CalendarBlank size={13} />} {task.due_date}{task.due_time ? ` ${task.due_time}` : ""}
        </span>
      )}
      <button
        data-testid={`task-delete-${task.id}`}
        onClick={(e) => { e.stopPropagation(); onDelete(task.id); }}
        className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
      >
        <Trash size={16} />
      </button>
    </div>
  );
}

// Only this many completed tasks are shown by default; the rest stay
// collapsed behind an explicit "show more" toggle so a long completed
// history doesn't push the incomplete list off-screen.
const COMPLETED_PAGE_SIZE = 10;

export default function TasksPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [newDueDate, setNewDueDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailTask, setDetailTask] = useState(null);
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const [overdueDialogOpen, setOverdueDialogOpen] = useState(false);
  // Favorite-note editing, opened directly from the sidebar's Favorite
  // Notes widget so the user never leaves the Tasks page/URL for it -
  // closing this dialog lands them right back on Tasks, no extra
  // navigation needed. `favoritesRefreshKey` is bumped on close so the
  // sidebar's favorites list picks up any title/content change (or
  // disappears if it got un-favorited/deleted) without a full page reload.
  const [viewingCapture, setViewingCapture] = useState(null);
  const [favoritesRefreshKey, setFavoritesRefreshKey] = useState(0);
  const { overdueTasks } = useTaskReminders(tasks);
  const overdueIds = new Set(overdueTasks.map((tk) => tk.id));
  const todayStr = new Date().toISOString().slice(0, 10);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const loadTasks = async () => {
    const data = await listTasks();
    setTasks(data);
    setLoading(false);
  };

  useEffect(() => {
    loadTasks();
  }, []);

  // Deep-link support for the global search dropdown - it navigates here
  // with ?openTask=<id> for unscheduled tasks (scheduled ones deep-link
  // into /schedule instead, since this page hides those). Waits for the
  // task list to actually load before looking it up.
  useEffect(() => {
    const openTaskId = new URLSearchParams(location.search).get("openTask");
    if (!openTaskId || tasks.length === 0) return;
    const found = tasks.find((tk) => tk.id === openTaskId);
    if (found) setDetailTask(found);
    navigate("/tasks", { replace: true });
  }, [location.search, tasks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      const data = await createTask({ title: newTitle.trim(), due_date: newDueDate || null });
      setTasks((prev) => [...prev, data]);
      setNewTitle("");
      setNewDueDate("");
    } catch (e) {
      toast.error(t("tasks.createFailedError"));
    }
  };

  // Quick-add from the "日行程表" sidebar widget: always today's date, an
  // explicit time slot (that's the whole point of that widget - a
  // same-day timeline), title only.
  const handleCreateScheduleItem = async ({ title, due_time }) => {
    try {
      const data = await createTask({ title: title.trim(), due_date: todayStr, due_time });
      setTasks((prev) => [...prev, data]);
    } catch (e) {
      toast.error(t("tasks.createFailedError"));
    }
  };

  const handleOpenFavoriteCapture = async (captureId) => {
    try {
      const { data } = await api.get(`/captures/${captureId}`);
      setViewingCapture(data);
    } catch (e) {
      toast.error(t("common.genericError"));
    }
  };

  const handleViewingCaptureOpenChange = (isOpen) => {
    if (!isOpen) {
      setViewingCapture(null);
      setFavoritesRefreshKey((k) => k + 1);
    }
  };

  const handleViewingCaptureDeleted = async (captureId) => {
    try {
      await deleteCapture(captureId);
      toast.success(t("dashboard.movedToTrashSuccess"));
    } catch (e) {
      toast.error(t("common.genericError"));
    } finally {
      setViewingCapture(null);
      setFavoritesRefreshKey((k) => k + 1);
    }
  };

  const handleToggleComplete = async (task) => {
    try {
      const data = await updateTask(task.id, { completed: !task.completed });
      setTasks((prev) => prev.map((tk) => (tk.id === task.id ? data : tk)));
    } catch (e) {
      toast.error(t("tasks.updateFailedError"));
    }
  };

  // Opening a task from inside the overdue dialog closes the dialog first,
  // so TaskDetailDialog doesn't stack on top of it.
  const handleOpenOverdueTaskDetail = (task) => {
    setOverdueDialogOpen(false);
    setDetailTask(task);
  };

  const handleDelete = async (id) => {
    try {
      await deleteTask(id);
      setTasks((prev) => prev.filter((tk) => tk.id !== id));
    } catch (e) {
      toast.error(t("tasks.deleteFailedError"));
    }
  };

  // Tasks with a due date now "live" in the Schedule module (they show up
  // there on their scheduled day) - hiding them here keeps this page a
  // pure backlog of not-yet-scheduled work, instead of duplicating what's
  // already visible on the calendar.
  const unscheduled = tasks.filter((tk) => !tk.due_date);
  const incomplete = unscheduled.filter((tk) => !tk.completed);
  const completed = unscheduled.filter((tk) => tk.completed);
  const hasMoreCompleted = completed.length > COMPLETED_PAGE_SIZE;
  const visibleCompleted = showAllCompleted ? completed : completed.slice(0, COMPLETED_PAGE_SIZE);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = incomplete.findIndex((tk) => tk.id === active.id);
    const newIndex = incomplete.findIndex((tk) => tk.id === over.id);
    const reordered = arrayMove(incomplete, oldIndex, newIndex);
    setTasks((prev) => [...reordered, ...prev.filter((tk) => tk.completed)]);
    try {
      await reorderTasks(reordered.map((tk) => tk.id));
    } catch (e) {
      toast.error(t("tasks.reorderFailedError"));
      loadTasks();
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="tasks-page">
      <ModuleNavBar active="tasks" />
      <div className="flex-1 overflow-y-auto p-3 md:p-5">
        <div className="max-w-[1600px] mx-auto w-full flex gap-4 min-h-full">
        <div className="flex-1 max-w-2xl">
        <h1 className="font-display text-xl md:text-2xl font-black tracking-tight flex items-center gap-2 mb-2">
          <CheckSquare size={20} /> {t("modules.tasks")}
        </h1>

        {overdueTasks.length > 0 && (
          <button
            type="button"
            data-testid="overdue-tasks-banner"
            onClick={() => setOverdueDialogOpen(true)}
            className="flex items-center gap-2 text-sm font-bold text-red-600 mb-2 border border-red-500/40 bg-red-500/10 px-2.5 py-1.5 hover:bg-red-500/20 transition-colors w-full text-left"
          >
            <BellRinging size={15} weight="fill" /> {t("reminders.overdueBanner", { count: overdueTasks.length })}
          </button>
        )}

        <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2 mb-3">
          <Input
            data-testid="new-task-input"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t("tasks.newTaskPlaceholder")}
            className="rounded-none flex-1 font-bold text-sm"
          />
          <Input
            data-testid="new-task-due-date-input"
            type="date"
            value={newDueDate}
            onChange={(e) => setNewDueDate(e.target.value)}
            className="rounded-none sm:w-40 font-bold text-sm"
          />
          <Button type="submit" data-testid="new-task-submit-button" className="rounded-none gap-2 font-bold text-sm">
            <Plus size={16} weight="bold" /> {t("tasks.addTask")}
          </Button>
        </form>

        {!loading && unscheduled.length === 0 && (
          <p className="text-center text-muted-foreground py-16 font-bold" data-testid="empty-tasks-state">{t("tasks.emptyTasks")}</p>
        )}

        {incomplete.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={incomplete.map((tk) => tk.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5 mb-3" data-testid="incomplete-tasks-list">
                {incomplete.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    draggable
                    isOverdue={overdueIds.has(task.id)}
                    isDueToday={task.due_date === todayStr && !task.completed}
                    onToggle={handleToggleComplete}
                    onDelete={handleDelete}
                    onOpenDetail={setDetailTask}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {completed.length > 0 && (
          <div>
            <p className="font-mono-tag text-xs md:text-sm font-bold text-muted-foreground mb-1.5">{t("tasks.completedSectionTitle")}</p>
            <div className="space-y-1.5" data-testid="completed-tasks-list">
              {visibleCompleted.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  isOverdue={false}
                  isDueToday={false}
                  onToggle={handleToggleComplete}
                  onDelete={handleDelete}
                  onOpenDetail={setDetailTask}
                />
              ))}
            </div>
            {hasMoreCompleted && (
              <button
                data-testid="completed-tasks-toggle-button"
                onClick={() => setShowAllCompleted((prev) => !prev)}
                className="flex items-center gap-1 text-xs md:text-sm font-bold text-muted-foreground hover:text-foreground transition-colors mt-2"
              >
                {showAllCompleted ? (
                  <>
                    <CaretUp size={14} weight="bold" /> {t("tasks.collapseCompleted")}
                  </>
                ) : (
                  <>
                    <CaretDown size={14} weight="bold" /> {t("tasks.showMoreCompleted", { count: completed.length - COMPLETED_PAGE_SIZE })}
                  </>
                )}
              </button>
            )}
          </div>
        )}
        </div>
        <TasksSidePanel
          tasks={tasks}
          onCreateScheduleItem={handleCreateScheduleItem}
          onOpenFavoriteCapture={handleOpenFavoriteCapture}
          favoritesRefreshKey={favoritesRefreshKey}
          onOpenTaskDetail={setDetailTask}
        />
        </div>
      </div>

      <Dialog open={overdueDialogOpen} onOpenChange={setOverdueDialogOpen}>
        <DialogContent className="rounded-none max-w-md max-h-[75vh] overflow-y-auto" data-testid="overdue-tasks-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-600">
              <BellRinging size={18} weight="fill" /> {t("taskSidebar.overdueTasksTitle")}
            </DialogTitle>
            <DialogDescription>{t("reminders.overdueBanner", { count: overdueTasks.length })}</DialogDescription>
          </DialogHeader>
          <OverdueTasksCard
            overdueTasks={overdueTasks}
            onToggle={handleToggleComplete}
            onOpenDetail={handleOpenOverdueTaskDetail}
            previewSize={overdueTasks.length}
          />
        </DialogContent>
      </Dialog>

      <TaskDetailDialog
        task={detailTask}
        open={!!detailTask}
        onOpenChange={(o) => !o && setDetailTask(null)}
        onSaved={(updated) => setTasks((prev) => prev.map((tk) => (tk.id === updated.id ? updated : tk)))}
        onDeleted={(id) => setTasks((prev) => prev.filter((tk) => tk.id !== id))}
      />

      <CaptureEditDialog
        capture={viewingCapture}
        open={!!viewingCapture}
        onOpenChange={handleViewingCaptureOpenChange}
        onDelete={handleViewingCaptureDeleted}
        onSaved={setViewingCapture}
      />
    </div>
  );
}
